import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { useDishesQuery } from '@/features/menu-management/Dishes/hooks/useDishesQuery'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'

/**
 * The menu, as options for the reward picker.
 *
 * Labelled in the venue's default language rather than the interface language,
 * the same choice the category picker makes: the list is a list of the
 * restaurant's own records, and a dish that has no translation into the language
 * the manager happens to be using is still that dish.
 */
export function useRewardDishOptions() {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguage } = useSelectedLanguagesQuery(currentLanguage)
  const { dishesList, dishesListLoading } = useDishesQuery()

  const dishOptions = useMemo(
    () =>
      (dishesList ?? [])
        .filter(dish => !!dish.id)
        .map(dish => ({
          label:
            dish.translations.find(
              translation =>
                translation.languageCode === defaultLanguage?.languageCode
            )?.name ?? '',
          value: dish.id as string
        }))
        // A dish with no name in the default language would render as a blank
        // chip the manager cannot tell apart from any other blank one.
        .filter(option => option.label.length > 0) as OptionType[],
    [dishesList, defaultLanguage?.languageCode]
  )

  return { dishOptions, dishOptionsLoading: dishesListLoading }
}
